import {
  COLUMN_SYNONYMS,
  describeFuzzyMatches,
  describeMissingColumns,
  isTotalRowLabel,
  isUsableHeaderRow,
  repeatRowAddsHeadcount,
  resolveColumns,
} from '@shared/column-matching'
import type { ColumnField, ExcelParseResult, ParseNotice } from '@shared/column-matching'
import {
  MAX_INVALID_FTE_NOTICES,
  buildInvalidFteNotice,
  findDuplicateRoles,
  findSimilarRoles,
  foldRoleKey,
  isInvalidFteValue,
  normalizeFteCell,
} from '@shared/roster-validation'
import type { RoleOccurrence } from '@shared/roster-validation'
import type { DashboardData, Task, WorkforceMode } from '@shared/types'

const CSV_QUOTED_FIELD_HINT =
  ' If a quoted field contains a comma (for example "Manager, Finance"), this CSV reader splits it into two columns and shifts the cells; save the file as XLSX instead.'

const EMPTY: DashboardData = { subfunctions: [], automation_builds: [] }

const VALID_MODES = new Set<WorkforceMode>(['Human', 'Human+Machine', 'RPA', 'GenAI', 'Agentic'])
const MODE_ALIASES: Record<string, WorkforceMode> = {
  hybrid: 'Human+Machine', onsite: 'Human', remote: 'Human+Machine',
  manual: 'Human', automated: 'RPA', 'ai-assisted': 'GenAI',
  human: 'Human', 'human+machine': 'Human+Machine', rpa: 'RPA', genai: 'GenAI', agentic: 'Agentic',
}

function normalizeMode(raw: string): WorkforceMode {
  const s = raw.trim()
  if (VALID_MODES.has(s as WorkforceMode)) return s as WorkforceMode
  return MODE_ALIASES[s.toLowerCase()] ?? 'Human'
}

/**
 * Parse CSV text using a pre-resolved column mapping. The only caller,
 * parseCsvToExcelResult, has already checked for a data row and a usable header row.
 */
function parseCsvWithResolution(
  text: string,
  resolution: ReturnType<typeof resolveColumns>
): ExcelParseResult {
  const lines = text.trim().split('\n')
  // Leading blank lines are trimmed away, so reported rows are shifted back by them.
  const rowOffset = (text.slice(0, text.length - text.trimStart().length).match(/\n/g) ?? []).length

  const warnings = describeFuzzyMatches(resolution)

  const read = (cells: string[], field: ColumnField): string => {
    for (const match of resolution.matches.get(field) ?? []) {
      const value = cells[match.index]
      if (value) return value
    }
    return ''
  }

  const subfunctionMap: Record<string, Record<string, { ftes: number; current_tasks: Task[] }>> = {}
  const excludedTotalRows: Array<{ label: string; fte: string }> = []
  const mergedRoleKeys = new Set<string>()
  const roleOccurrences: RoleOccurrence[] = []
  const pairsSeen = new Set<string>()
  const invalidFteNotices: ParseNotice[] = []
  let invalidFteTotal = 0
  const fteHeader = resolution.matches.get('fte')?.[0]?.header ?? 'FTE'
  let rowsImported = 0

  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map((v) => v.trim().replace(/^"|"$/g, ''))
    const sfNameRaw = read(cells, 'subfunction')
    const roleNameRaw = read(cells, 'role')

    if (isTotalRowLabel(sfNameRaw) || isTotalRowLabel(roleNameRaw)) {
      excludedTotalRows.push({ label: isTotalRowLabel(sfNameRaw) ? sfNameRaw : roleNameRaw, fte: read(cells, 'fte') })
      continue
    }

    const sfName = sfNameRaw || 'Unknown'
    const roleName = roleNameRaw || 'Unknown Role'
    const fteRaw = read(cells, 'fte')
    const fteCell = normalizeFteCell(fteRaw)
    const ftes = parseFloat(fteCell || '1') || 1
    const taskName = read(cells, 'task') || read(cells, 'description')
    rowsImported++

    // Folded key; task-only repeats are not duplicates and their FTE is ignored.
    const pairKey = foldRoleKey(sfName, roleName)
    const firstSeen = !pairsSeen.has(pairKey)
    pairsSeen.add(pairKey)
    const contributesHeadcount =
      firstSeen || repeatRowAddsHeadcount({ roleNamedInRow: Boolean(roleNameRaw), hasTask: Boolean(taskName) })
    if (contributesHeadcount) {
      const row = i + 1 + rowOffset
      roleOccurrences.push({ subfunction: sfName, role: roleName, row, ftes: fteCell ? ftes : undefined })
      if (isInvalidFteValue(fteRaw)) {
        invalidFteTotal++
        if (invalidFteNotices.length < MAX_INVALID_FTE_NOTICES) {
          const notice = buildInvalidFteNotice({ row, column: fteHeader, value: fteRaw })
          invalidFteNotices.push({ ...notice, message: notice.message + CSV_QUOTED_FIELD_HINT })
        }
      }
    }

    if (!subfunctionMap[sfName]) subfunctionMap[sfName] = {}
    const existingRole = subfunctionMap[sfName][roleName]
    if (!existingRole) {
      subfunctionMap[sfName][roleName] = { ftes, current_tasks: [] }
    } else if (repeatRowAddsHeadcount({ roleNamedInRow: Boolean(roleNameRaw), hasTask: Boolean(taskName) })) {
      existingRole.ftes += ftes
      mergedRoleKeys.add(`${sfName}\u0000${roleName}`)
    }

    if (taskName) {
      const mode = normalizeMode(read(cells, 'mode') || 'Human')
      subfunctionMap[sfName][roleName].current_tasks.push({
        number: String(subfunctionMap[sfName][roleName].current_tasks.length + 1),
        name: taskName,
        skills: read(cells, 'skills'),
        time_pct: read(cells, 'time_pct') || '5%',
        hours: read(cells, 'hours') || '100',
        cost: read(cells, 'cost') || '$0',
        value: read(cells, 'value') || '3',
        mode,
        mode_raw: `🟩 ${mode}`,
      })
    }
  }

  for (const excluded of excludedTotalRows) {
    const amount = excluded.fte ? ` carrying ${excluded.fte} FTE` : ''
    warnings.push({
      code: 'total-row-excluded',
      detectedHeader: excluded.label,
      message: `A total row labelled "${excluded.label}"${amount} was excluded. Counting it as a subfunction would have added the whole workforce a second time.`,
    })
  }
  if (mergedRoleKeys.size > 0) {
    warnings.push({
      code: 'duplicate-roles-merged',
      message: `${mergedRoleKeys.size} role${mergedRoleKeys.size > 1 ? 's' : ''} appeared on more than one headcount row and their FTE values were added together. Rows that only add a task to an existing role were not counted twice.`,
    })
  }
  if (rowsImported > 0 && !resolution.matches.get('fte')?.length) {
    warnings.push({
      code: 'headcount-column-missing',
      field: 'fte',
      detectedHeaders: resolution.detectedHeaders,
      message: `No headcount column was recognised, so every row counts as 1 FTE and the total shown is a count of rows, not of people. Rename your headcount column to one of: ${COLUMN_SYNONYMS.fte.join(', ')} — or map it using the column dropdown.`,
    })
  }

  const errors: ParseNotice[] = [...invalidFteNotices]
  if (invalidFteTotal > invalidFteNotices.length) {
    errors.push({
      code: 'invalid-cell-value',
      field: 'fte',
      message: `${invalidFteTotal - invalidFteNotices.length} more rows have an invalid headcount value and are not listed.`,
    })
  }

  return {
    data: {
      subfunctions: Object.entries(subfunctionMap).map(([name, roles]) => ({ name, roles })),
      automation_builds: [],
    },
    warnings,
    errors,
    duplicateRoles: findDuplicateRoles(roleOccurrences),
    similarRoles: findSimilarRoles(roleOccurrences),
  }
}

/** Parse a CSV text string into an ExcelParseResult. */
export function parseCsvToExcelResult(text: string): ExcelParseResult {
  const lines = text.trim().split('\n')
  if (lines.length < 2) {
    return {
      data: EMPTY,
      warnings: [],
      errors: [{ code: 'no-data-rows', message: 'This CSV file appears to be empty or has no data rows.' }],
      sourceRowCount: 0,
    }
  }

  const rawHeaders = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''))
  const resolution = resolveColumns(rawHeaders)
  if (!isUsableHeaderRow(resolution)) {
    return { data: EMPTY, warnings: [], errors: describeMissingColumns(resolution), rawHeaders, sourceRowCount: lines.length - 1 }
  }

  return { ...parseCsvWithResolution(text, resolution), rawHeaders, sourceRowCount: lines.length - 1 }
}
